import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { LOOM_MEETING_NUDGE_SIDENAV_TRIGGER_ID } from '@post-office/message-template--twc-loom-meeting-nudge-sidenav/reminder-event';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import {
	getContentKeywords,
	getTitleKeywords,
} from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalKeywords.ts';
import { matchMeetingSignal } from '@confluence/experiment-meeting-signal-core/entry-points/matchMeetingSignal.ts';
import { matchesMeetingTemplate } from '@confluence/experiment-meeting-signal-core/entry-points/matchesMeetingTemplate.ts';
import type {
	MatchedMeetingSignal,
	MeetingSignalInput,
	MeetingSignalPageType,
	MeetingSignalTrigger,
} from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { useIsHighOrLowTouchTwcSignup } from './useIsHighOrLowTouchTwcSignup.ts';
import { useMeetingRecorderEligibility } from './useMeetingRecorderEligibility.ts';
import { useMeetingSignalErrorAnalytics } from './useMeetingSignalErrorAnalytics.ts';
import { useMeetingSignalReminderRequestListener } from './useMeetingSignalReminderRequestListener.ts';
import { useLoomNudgeDailyLimit } from './useLoomNudgeDailyLimit.ts';

type MeetingSignalRenderState =
	| { matched: false; matchedSignal: null; pageType: null; trigger: null }
	| {
			matched: true;
			matchedSignal: MatchedMeetingSignal;
			pageType: MeetingSignalPageType;
			trigger: MeetingSignalTrigger;
	  };

const INITIAL_STATE: MeetingSignalRenderState = {
	matched: false,
	matchedSignal: null,
	pageType: null,
	trigger: null,
};

const getMatchedSignal = (
	input: MeetingSignalInput,
	titleKeywords: readonly string[],
	contentKeywords: readonly string[],
): MatchedMeetingSignal | null => {
	if (input.titleText && matchMeetingSignal(input.titleText, titleKeywords)) {
		return 'title';
	}
	if (input.contentText && matchMeetingSignal(input.contentText, contentKeywords)) {
		return 'content';
	}
	if (matchesMeetingTemplate(input.template)) {
		return 'template';
	}
	return null;
};

export type UseMeetingSignalExperimentResult = MeetingSignalRenderState & {
	evaluate: (input: MeetingSignalInput) => void;
};

type UseMeetingSignalExperimentOptions = {
	canExpose?: boolean;
};

/**
 * Checks whether a meeting signal can show a nudge.
 *
 * The hook reads the cohort gate and preloaded Meeting Recorder eligibility. It then checks the
 * meeting signal and exposes the HT or LT experiment. The hook keeps the first treatment match for
 * the current page load.
 *
 * A title-edit match shows the title nudge. All other matches trigger the side-nav nudge.
 */
export function useMeetingSignalExperiment({
	canExpose = true,
}: UseMeetingSignalExperimentOptions = {}): UseMeetingSignalExperimentResult {
	useMeetingSignalReminderRequestListener();
	const intl = useIntl();
	const titleKeywords = useMemo(() => getTitleKeywords(intl), [intl]);
	const contentKeywords = useMemo(() => getContentKeywords(intl), [intl]);
	const tapTraits = useIsHighOrLowTouchTwcSignup();
	const { isHighTouch, isLowTouch } = tapTraits;
	const renderServerPlaceholder = useIsSSROrHydrating();
	const dailyLimit = useLoomNudgeDailyLimit();
	const { canShowLoomNudge } = dailyLimit;

	// Recorder eligibility owns the cohort-specific rollout gate check before reading preload data.
	const meetingRecorderEligibility = useMeetingRecorderEligibility({
		isHighTouch: renderServerPlaceholder ? null : isHighTouch,
		isLowTouch: renderServerPlaceholder ? null : isLowTouch,
	});
	const { isAvailable } = meetingRecorderEligibility;
	useMeetingSignalErrorAnalytics({
		tapTraits,
		dailyLimit,
		meetingRecorderEligibility,
		skip: renderServerPlaceholder,
	});
	const { fireTrigger } = useFirePostOfficeTrigger({
		triggerId: LOOM_MEETING_NUDGE_SIDENAV_TRIGGER_ID,
	});

	// Attach points can report a signal before cohort and recorder eligibility have resolved.
	// Retain the latest candidate so the effect can evaluate it once those prerequisites are ready.
	const [signalInputToEvaluate, setSignalInputToEvaluate] = useState<MeetingSignalInput | null>(
		null,
	);
	const [state, setState] = useState<MeetingSignalRenderState>(INITIAL_STATE);
	const hasMatchedAlreadyRef = useRef(false);

	const evaluate = useCallback((input: MeetingSignalInput) => {
		if (!hasMatchedAlreadyRef.current) {
			setSignalInputToEvaluate(input);
		}
	}, []);

	useEffect(() => {
		if (
			!canExpose ||
			!isAvailable ||
			!canShowLoomNudge ||
			!signalInputToEvaluate ||
			hasMatchedAlreadyRef.current
		) {
			return;
		}

		const matchedSignal = getMatchedSignal(signalInputToEvaluate, titleKeywords, contentKeywords);
		if (!matchedSignal) {
			return;
		}

		const isExperimentEnabled =
			isHighTouch === true
				? expValEquals('twcg-2202-loom-meeting-nudge-ht', 'isEnabled', true)
				: isLowTouch === true && expValEquals('twcg-2202-loom-meeting-nudge-lt', 'isEnabled', true);

		if (!isExperimentEnabled) {
			return;
		}

		hasMatchedAlreadyRef.current = true;
		setState({
			matched: true,
			matchedSignal,
			pageType: signalInputToEvaluate.pageType,
			trigger: signalInputToEvaluate.trigger,
		});
		if (signalInputToEvaluate.trigger !== 'titleEdit') {
			fireTrigger({ isActive: true });
		}
	}, [
		canExpose,
		canShowLoomNudge,
		contentKeywords,
		fireTrigger,
		isAvailable,
		isHighTouch,
		isLowTouch,
		signalInputToEvaluate,
		titleKeywords,
	]);

	return { ...state, evaluate };
}
